import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { createAssignment } from "../../worksheetSlice";
import { hasSectionSourceData } from "../../../../utils/worksheetUtils";
import {
  ASSIGNMENT_PLAN_REQUIRED_MESSAGE,
  canCreateAssignment,
  hasDirectPlusAddon,
  DIRECT_PLUS_REQUIRED_MESSAGE,
} from "../../../../utils/planUtils";
import {
  DEMO_STUDENT_LIST,
  buildDemoResultMessage,
} from "../../../../utils/demoUtils";

import Modal from "../../../../components/Modal";
import StepProgressBar from "../../../../components/StepProgressBar";

import TypeSelectionPage from "../common/TypeSelectionPage/TypeSelectionPage";
import ParagraphSelectionPage from "./ParagraphSelectionPage/ParagraphSelectionPage";
import DateSelectionPage from "./DateSelectionPage/DateSelectionPage";
import StudentSelectionPage from "./StudentSelectionPage/StudentSelectionPage";
import TitleSelectionPage from "./TitleSelectionPage/TitleSelectionPage";

import "../../../../styles/WorksheetModalV2.css";
import { useDefaultRewardPoints } from "../../../reward/RewardPointsField";

// 복습(무한복습 포함)을 켤 수 있는 시험지 유형.
// **서버의 `api/utils/assignment/wrong_answer.py` 와 같은 목록이어야 한다.**
// 여기서 보내지 않으면 서버가 값을 못 받고, 서버가 막으면 화면만 켜져 있게 된다.
// 지문분석은 지금 구문분석 섹션만 복습으로 나간다(나머지는 문항 짝이 맞지 않는다).
const RETRY_SUPPORTED_TYPE_LIST = [
  "vocabulary",
  "paragraph",
  "grammar",
  "sentence",
  "comprehensive",
  "analysis",
];

/**
 * ── 변경사항 ──
 * 1. StepProgressBar — 현재 단계 표시 + 완료 단계 클릭 시 되돌아가기
 * 2. getStepsForType() — worksheet.type별 단계 목록 한 곳에서 관리
 * 3. handlePrev() — 모든 페이지에 onClickPrev 전달
 * 4. StudentSelectionPage에 루틴 미리보기용 props 전달
 * 5. createAssignment 등 비즈니스 로직은 기존 그대로
 */
const WorksheetModal = ({ onClose, isDemo = false }) => {
  const dispatch = useDispatch();

  const { worksheet } = useSelector((state) => state.worksheet);
  const { user } = useSelector((state) => state.auth);

  const [currentStep, setCurrentStep] = useState("title");

  // ===== [Step 1 상태] =====
  const [selectedParagraphs, setSelectedParagraphs] = useState(() => {
    if (worksheet?.type === "sentence") return [0];
    return Array.isArray(worksheet?.data)
      ? worksheet.data.map((_, i) => i)
      : [0];
  });
  const [selectedTypes, setSelectedTypes] = useState([]);

  // ===== [Step 2 상태] =====
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [hideAfterClosed, setHideAfterClosed] = useState(true);

  // ===== [Step 3 상태] =====
  const [selectedStudents, setSelectedStudents] = useState([]);

  // ===== [완료 포인트] 학원 관리 > 포인트 의 기본값으로 채운다. 체험은 포인트가 없다 =====
  const [rewardPoints, setRewardPoints] = useState(100);
  const [rewardMinScore, setRewardMinScore] = useState(0);
  useDefaultRewardPoints(isDemo ? "" : worksheet?.type, setRewardPoints, setRewardMinScore);

  // ===== [복습 설정 상태] =====
  const [isRetryEnabled, setIsRetryEnabled] = useState(true);
  const [isAutoRetryEnabled, setIsAutoRetryEnabled] = useState(false);
  const [maxRetries, setMaxRetries] = useState(3);

  const steps = useMemo(() => getStepsForType(worksheet?.type), [
    worksheet?.type,
  ]);

  // 목록에서 이미 막지만, 다른 경로로 모달이 열리는 경우를 대비한 이중 방어다.
  // 예시 문제지는 플랜과 무관하게 체험할 수 있으므로 검사하지 않는다.
  useEffect(() => {
    if (!isDemo && !canCreateAssignment(user.plan)) {
      alert(ASSIGNMENT_PLAN_REQUIRED_MESSAGE);
      onClose();
    }
  }, [isDemo, user.plan, onClose]);

  // 체험은 고를 학생이 가상 학생 한 명뿐이라 미리 골라 둔다.
  useEffect(() => {
    if (isDemo) {
      setSelectedStudents(DEMO_STUDENT_LIST);
    }
  }, [isDemo]);

  const handleDateSelect = (date) => setSelectedDate(date);
  const handleStep = (step) => setCurrentStep(step);

  const handlePrev = () => {
    const idx = steps.findIndex((s) => s.key === currentStep);
    if (idx > 0) setCurrentStep(steps[idx - 1].key);
  };

  const handleStepClick = (stepKey) => {
    const targetIdx = steps.findIndex((s) => s.key === stepKey);
    const currentIdx = steps.findIndex((s) => s.key === currentStep);
    if (targetIdx < currentIdx) setCurrentStep(stepKey);
  };

  const handleTypeClick = (type) => {
    // 직독직해는 직독직해+ 옵션 가입 학원만 실제 출제할 수 있다. 체험 문제지는
    // 학생 미리보기로만 열리므로 그대로 눌러볼 수 있게 둔다. 해제는 늘 된다.
    if (
      type === "direct_translation" &&
      !isDemo &&
      !hasDirectPlusAddon(user) &&
      !selectedTypes.includes(type)
    ) {
      alert(DIRECT_PLUS_REQUIRED_MESSAGE);
      return;
    }
    setSelectedTypes((prev) => {
      if (prev.includes(type)) return prev.filter((item) => item !== type);
      return [...prev, type];
    });
  };

  const handleCreateAssignment = async () => {
    let worksheetData = [];
    if (worksheet.type === "comprehensive") {
      const examIndex =
        selectedTypes.find((type) => type.key === "comprehensive")?.index || 1;
      worksheetData = worksheet.data[examIndex - 1];
    } else {
      worksheetData = worksheet.data;
    }

    let finalMaxRetries = undefined;
    let finalIsRetryEnabled = undefined;
    let finalIsAutoRetryEnabled = undefined;
    if (worksheet.type === "vocabulary") {
      const examType = selectedTypes.find((type) => type.key === "exam");
      finalMaxRetries = examType?.maxRetries ?? 3;
      finalIsRetryEnabled = examType?.isRetryEnabled !== false;
      finalIsAutoRetryEnabled = examType?.isAutoRetryEnabled !== false;
    } else if (RETRY_SUPPORTED_TYPE_LIST.includes(worksheet.type)) {
      // 모고·어법·문장테스트·내용이해·지문분석은 화면 하나에서 한 벌로 정한다.
      finalMaxRetries = maxRetries;
      finalIsRetryEnabled = isRetryEnabled;
      finalIsAutoRetryEnabled = isAutoRetryEnabled;
    }

    const paragraphs = Array.isArray(worksheetData)
      ? worksheetData.filter((_, i) => selectedParagraphs.includes(i))
      : worksheetData;

    // 지인에듀에서 시험지 본문을 못 받아오면 지문 껍데기만 남는다.
    // 이대로 출제하면 학생이 풀 수 없는 빈 과제가 되므로 여기서 막는다.
    if (!hasSectionSourceData(paragraphs)) {
      alert(
        "시험지 내용을 불러오지 못했습니다.\n페이지를 새로고침한 뒤 다시 출제해주세요."
      );
      return;
    }

    const data = {
      students: selectedStudents,
      closed_at: selectedDate,
      data: {
        paragraphs: paragraphs,
        types: selectedTypes,
      },
      type: worksheet.type,
      title: worksheet.title,
      author_name: worksheet.author_name,
      hide_after_closed: hideAfterClosed,
      ginedu_id: worksheet.id,
      reward_points: rewardPoints,
      reward_min_score: rewardMinScore,
      ...(RETRY_SUPPORTED_TYPE_LIST.includes(worksheet.type)
        ? {
            max_retries: finalMaxRetries,
            is_retry_enabled: finalIsRetryEnabled,
            is_auto_retry_enabled: finalIsAutoRetryEnabled,
          }
        : {}),
    };
    const result = await dispatch(createAssignment({ data }));

    // 서버가 400 을 줘도 예전에는 출제되었다고 알렸다. 실패는 실패로 알린다.
    if (createAssignment.rejected.match(result)) {
      alert(result.payload || "과제 출제에 실패했습니다. 다시 시도해주세요.");
      return;
    }

    // 체험은 학생 링크를 알려주는 것까지가 한 묶음이다. 서버가 응답에 실어 준다.
    if (isDemo) {
      alert(buildDemoResultMessage(worksheet.title, result.payload?.demo));
      onClose();
      return;
    }

    alert(`${worksheet.title} 과제가 출제되었습니다.`);
    onClose();
  };

  const renderContent = () => {
    if (currentStep === "paragraph")
      return (
        <ParagraphSelectionPage
          worksheet={worksheet}
          onClose={onClose}
          onClickPrev={handlePrev}
          onClickNext={() => {
            if (selectedParagraphs.length === 0)
              return alert("지문을 선택해주세요.");
            if (worksheet.type === "analysis") handleStep("type");
            else if (worksheet.type === "comprehensive") handleStep("date");
            else if (worksheet.type === "paragraph") handleStep("date");
          }}
          selectedParagraphs={selectedParagraphs}
          setSelectedParagraphs={setSelectedParagraphs}
          selectedTypes={selectedTypes}
          handleTypeClick={handleTypeClick}
          isRetryEnabled={isRetryEnabled}
          setIsRetryEnabled={setIsRetryEnabled}
          isAutoRetryEnabled={isAutoRetryEnabled}
          setIsAutoRetryEnabled={setIsAutoRetryEnabled}
          maxRetries={maxRetries}
          setMaxRetries={setMaxRetries}
        />
      );

    if (currentStep === "type")
      return (
        <TypeSelectionPage
          worksheet={worksheet}
          onClose={onClose}
          onClickPrev={handlePrev}
          onClickNext={() => {
            if (selectedTypes.length === 0)
              return alert("출제 유형을 선택해주세요.");
            if (selectedTypes.some((t) => t.key === "syntax_analysis")) {
              if (
                selectedTypes.find((t) => t.key === "syntax_analysis").level
                  .key === ""
              )
                return alert("구문분석 난이도를 선택해주세요.");
            }
            if (selectedTypes.some((t) => t.key === "direct_translation")) {
              if (
                selectedTypes.find((t) => t.key === "direct_translation")
                  .options.length === 0
              )
                return alert("직독직해 단계를 선택해주세요.");
            }
            if (selectedTypes.some((t) => t.key === "sentence_test")) {
              if (
                selectedTypes.find((t) => t.key === "sentence_test").options
                  .length === 0
              )
                return alert("문장학습 유형을 선택해주세요.");
            }
            if (worksheet.type === "comprehensive") handleStep("paragraph");
            else handleStep("date");
          }}
          selectedTypes={selectedTypes}
          setSelectedTypes={setSelectedTypes}
          isRetryEnabled={isRetryEnabled}
          setIsRetryEnabled={setIsRetryEnabled}
          isAutoRetryEnabled={isAutoRetryEnabled}
          setIsAutoRetryEnabled={setIsAutoRetryEnabled}
          maxRetries={maxRetries}
          setMaxRetries={setMaxRetries}
        />
      );

    if (currentStep === "date")
      return (
        <DateSelectionPage
          onClose={onClose}
          onClickPrev={handlePrev}
          onClickNext={() => handleStep("student")}
          selectedDate={selectedDate}
          handleDateSelect={handleDateSelect}
          hideAfterClosed={hideAfterClosed}
          setHideAfterClosed={setHideAfterClosed}
          rewardPoints={isDemo ? null : rewardPoints}
          setRewardPoints={setRewardPoints}
          rewardMinScore={rewardMinScore}
          setRewardMinScore={setRewardMinScore}
        />
      );

    if (currentStep === "student")
      return (
        <StudentSelectionPage
          onClose={onClose}
          onClickPrev={handlePrev}
          handleAddStudents={handleCreateAssignment}
          selectedStudents={selectedStudents}
          setSelectedStudents={setSelectedStudents}
          selectedDate={selectedDate}
          demoStudentList={isDemo ? DEMO_STUDENT_LIST : undefined}
          worksheet={worksheet}
          selectedTypes={selectedTypes}
          selectedParagraphs={selectedParagraphs}
          isRetryEnabled={isRetryEnabled}
          isAutoRetryEnabled={isAutoRetryEnabled}
          maxRetries={maxRetries}
        />
      );

    if (currentStep === "title")
      return (
        <TitleSelectionPage
          onClose={onClose}
          onClickNext={() => {
            if (worksheet.type === "paragraph") handleStep("paragraph");
            else if (
              ["vocabulary", "comprehensive", "sentence", "grammar"].includes(
                worksheet.type
              )
            )
              setCurrentStep("type");
            else handleStep("paragraph");
          }}
        />
      );
  };

  return (
    <Modal>
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth:
            currentStep === "paragraph"
              ? "min(1200px, calc(100vw - 40px))"
              : "min(560px, calc(100vw - 40px))",
          height: currentStep === "paragraph" ? "min(680px, 80vh)" : "auto",
          maxHeight: "min(calc(100vh - 40px), 80vh)",
          background: "#fff",
          borderRadius: "18px",
          overflow: "hidden",
          boxShadow: "0 10px 30px -22px rgba(20,40,50,.22)",
        }}
      >
        <StepProgressBar
          steps={steps}
          currentStep={currentStep}
          onStepClick={handleStepClick}
        />
        {renderContent()}
      </div>
    </Modal>
  );
};

/* ── worksheet.type별 단계 정의 ── */
function getStepsForType(type) {
  const title = { key: "title", label: "제목" };
  const paragraph = { key: "paragraph", label: "지문" };
  const typeStep = { key: "type", label: "유형" };
  const date = { key: "date", label: "마감일" };
  const student = { key: "student", label: "학생" };

  switch (type) {
    case "paragraph":
      return [title, paragraph, date, student];
    case "analysis":
      return [title, paragraph, typeStep, date, student];
    case "comprehensive":
      return [title, typeStep, paragraph, date, student];
    case "vocabulary":
    case "sentence":
    case "grammar":
      return [title, typeStep, date, student];
    default:
      return [title, paragraph, date, student];
  }
}

export default WorksheetModal;
